import type { DeviceCategory, DeviceStatus } from '@/types'

import { create } from 'zustand'

// Shared UI state for the 3D hall and the control interface around it. Initial
// values are static so the server render and the first client render match.

export type Focus = 'overview' | DeviceCategory
export type Lens = 'standard' | 'health' | 'thermal'
export type Phase = 'intro' | 'entering' | 'explore'
export type Quality = 'high' | 'low'

export type CameraPose = {
    x: number
    z: number
    yaw: number
}

type Options = { fromTour?: boolean }

type DatacenterState = {
    phase: Phase
    focus: Focus
    lens: Lens
    selectedRackId: string | null
    selectedDeviceId: string | null
    hoveredDeviceId: string | null
    hoveredRackId: string | null
    airflow: boolean
    traffic: boolean
    inventoryOpen: boolean
    search: string
    categoryFilter: DeviceCategory | 'all'
    statusFilter: DeviceStatus | 'all'
    touring: boolean
    tourStep: number
    flying: boolean
    cameraNonce: number
    pose: CameraPose
    quality: Quality
    reducedMotion: boolean
    sceneReady: boolean
    sceneFailed: boolean
    enter: () => void
    skipIntro: () => void
    finishEntering: () => void
    setFocus: (focus: Focus, options?: Options) => void
    selectRack: (rackId: string | null, options?: Options) => void
    selectDevice: (deviceId: string, rackId: string | null, options?: Options) => void
    back: () => void
    recenter: () => void
    setLens: (lens: Lens) => void
    toggleAirflow: () => void
    toggleTraffic: () => void
    setHoveredDevice: (deviceId: string | null) => void
    setHoveredRack: (rackId: string | null) => void
    setInventoryOpen: (open: boolean) => void
    setSearch: (search: string) => void
    setCategoryFilter: (category: DeviceCategory | 'all') => void
    setStatusFilter: (status: DeviceStatus | 'all') => void
    startTour: () => void
    stopTour: () => void
    setTourStep: (step: number) => void
    setFlying: (flying: boolean) => void
    setPose: (pose: CameraPose) => void
    setQuality: (quality: Quality) => void
    setReducedMotion: (reduced: boolean) => void
    setSceneReady: (ready: boolean) => void
    setSceneFailed: (failed: boolean) => void
}

export const useDatacenterStore = create<DatacenterState>()(set => ({
    phase: 'intro',
    focus: 'overview',
    lens: 'standard',
    selectedRackId: null,
    selectedDeviceId: null,
    hoveredDeviceId: null,
    hoveredRackId: null,
    airflow: false,
    traffic: true,
    inventoryOpen: false,
    search: '',
    categoryFilter: 'all',
    statusFilter: 'all',
    touring: false,
    tourStep: 0,
    flying: false,
    cameraNonce: 0,
    pose: { x: 0, z: 0, yaw: 0 },
    quality: 'high',
    reducedMotion: false,
    sceneReady: false,
    sceneFailed: false,

    enter: () => set(state => state.phase === 'intro' ? { phase: 'entering' } : {}),

    skipIntro: () => set(state => ({ phase: 'explore', cameraNonce: state.cameraNonce + 1 })),

    finishEntering: () => set({ phase: 'explore' }),

    setFocus: (focus, options) => set(state => ({
        focus,
        selectedRackId: null,
        selectedDeviceId: null,
        phase: 'explore',
        touring: options?.fromTour ? state.touring : false,
        cameraNonce: state.cameraNonce + 1
    })),

    selectRack: (rackId, options) => set(state => ({
        selectedRackId: rackId,
        selectedDeviceId: null,
        phase: 'explore',
        touring: options?.fromTour ? state.touring : false,
        cameraNonce: state.cameraNonce + 1
    })),

    selectDevice: (deviceId, rackId, options) => set(state => ({
        selectedDeviceId: deviceId,
        selectedRackId: rackId,
        phase: 'explore',
        touring: options?.fromTour ? state.touring : false,
        cameraNonce: state.cameraNonce + 1
    })),

    back: () => set(state => {
        if (state.selectedDeviceId) return { selectedDeviceId: null, touring: false, cameraNonce: state.cameraNonce + 1 }
        if (state.selectedRackId) return { selectedRackId: null, touring: false, cameraNonce: state.cameraNonce + 1 }
        if (state.focus !== 'overview') return { focus: 'overview', touring: false, cameraNonce: state.cameraNonce + 1 }

        return { touring: false }
    }),

    recenter: () => set(state => ({ cameraNonce: state.cameraNonce + 1 })),

    setLens: lens => set({ lens }),

    toggleAirflow: () => set(state => ({ airflow: !state.airflow })),

    toggleTraffic: () => set(state => ({ traffic: !state.traffic })),

    setHoveredDevice: deviceId => set(state => state.hoveredDeviceId === deviceId ? {} : { hoveredDeviceId: deviceId }),

    setHoveredRack: rackId => set(state => state.hoveredRackId === rackId ? {} : { hoveredRackId: rackId }),

    setInventoryOpen: inventoryOpen => set({ inventoryOpen }),

    setSearch: search => set({ search }),

    setCategoryFilter: categoryFilter => set({ categoryFilter }),

    setStatusFilter: statusFilter => set({ statusFilter }),

    startTour: () => set({ touring: true, tourStep: 0, phase: 'explore', inventoryOpen: false }),

    stopTour: () => set({ touring: false }),

    setTourStep: tourStep => set({ tourStep }),

    setFlying: flying => set(state => state.flying === flying ? {} : { flying }),

    setPose: pose => set({ pose }),

    setQuality: quality => set(state => state.quality === quality ? {} : { quality }),

    setReducedMotion: reducedMotion => set({ reducedMotion }),

    setSceneReady: sceneReady => set(state => state.sceneReady === sceneReady ? {} : { sceneReady }),

    setSceneFailed: sceneFailed => set({ sceneFailed })
}))
